import React, { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal'
import {
  LeavingGuard as BeforeUnloadLeavingGuard,
  ConfirmModal,
  useModalContext,
} from '@fourthwall/components'

type LeavingGuardProps = {
  when: boolean
  isValid?: boolean
  title?: string
  description?: string
  scope?: string
  onSave?: () => Promise<void>
}

const extractNavigationData = (event: Event): { href: string | null } => {
  const element = event.target as HTMLElement
  const anchor = element.closest('a') as HTMLAnchorElement | null

  if (anchor?.target === '_blank') {
    return { href: null }
  }

  return { href: anchor ? anchor.getAttribute('href') : null }
}

export const LeavingGuard = ({
  when,
  isValid,
  title = 'Are you sure you want to leave?',
  description = 'If you leave this page, all unsaved changes will be discarded.',
  scope,
  onSave,
}: LeavingGuardProps) => {
  // TODO: Use react-router useBlocker when createBrowserRouter is implemented
  const navigate = useNavigate()
  const { open, close } = useModalContext()

  const handleNavigation = useCallback(
    (event: Event) => {
      const { href } = extractNavigationData(event)

      if (!href) return

      if (scope && href.indexOf(scope) > -1) return

      event.preventDefault()

      if (!onSave || !isValid) {
        open(ConfirmModal, {
          title,
          text: description,
          confirmLabel: 'Yes, leave',
          cancelLabel: 'No, stay',
          confirmAppearance: 'destructive',
          onConfirm: () => navigate(href),
        })
      } else {
        open(UnsavedChangesModal, {
          onSave: async () => {
            await onSave()

            navigate(href)
          },
          onDiscard: () => navigate(href),
          onClose: close,
        })
      }
    },
    [onSave, isValid, open, title, description, navigate],
  )

  useEffect(() => {
    if (!when) return

    document.addEventListener('click', handleNavigation, true)

    return () => document.removeEventListener('click', handleNavigation, true)
  }, [when, handleNavigation])

  return <BeforeUnloadLeavingGuard when={when} />
}
